import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MicrosoftGraphFields } from '../../components/owner/MicrosoftGraphFields';
import {
  ArrowLeft, Mail, Save, Send, CheckCircle, AlertCircle,
  Shield, Eye, EyeOff, Server
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDER_HELP = {
  gmail: 'Use a Google App Password (not your login password). Enable 2-Step Verification, then create one at myaccount.google.com/apppasswords.',
  microsoft: 'Microsoft 365 no longer accepts passwords or app passwords for SMTP. Sending uses Microsoft Graph with an Azure (Entra ID) app registration — see setup steps below.',
};

const OwnerEmailSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [provider, setProvider] = useState('gmail');
  const [email, setEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [password, setPassword] = useState('');
  const [msTenantId, setMsTenantId] = useState('');
  const [msClientId, setMsClientId] = useState('');
  const [msClientSecret, setMsClientSecret] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/owner/email-settings`);
      setSettings(res.data);
      setProvider(res.data.provider || 'gmail');
      setEmail(res.data.email || '');
      setFromName(res.data.from_name || '');
      setMsTenantId(res.data.ms_tenant_id || '');
      setMsClientId(res.data.ms_client_id || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load email settings');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 6000);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { provider, email, from_name: fromName };
      if (provider === 'microsoft') {
        Object.assign(payload, { ms_tenant_id: msTenantId, ms_client_id: msClientId });
        if (msClientSecret) payload.ms_client_secret = msClientSecret;
      } else if (password) payload.password = password;
      const res = await axios.put(`${API_URL}/owner/email-settings`, payload);
      setSettings(res.data);
      setPassword('');
      setMsClientSecret('');
      showToast('success', 'Email settings saved. New emails will use this account immediately.');
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const payload = testEmail ? { to_email: testEmail } : {};
      const res = await axios.post(`${API_URL}/owner/email-settings/test`, payload);
      showToast('success', res.data.message);
    } catch (err) {
      showToast('error', err.response?.data?.detail || 'Test email failed');
    } finally {
      setTesting(false);
    }
  };

  const activePreset = settings?.available_providers?.find(p => p.key === provider);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading email settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="owner-email-settings-page">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/owner/dashboard')}
                    className="p-2 hover:bg-gray-100 rounded-lg" data-testid="back-to-dashboard">
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center space-x-3">
              <Mail className="text-blue-600" size={32} />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Platform Email Settings</h1>
                <p className="text-sm text-gray-500">Sender account used for portal OTPs and notification emails</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg max-w-md ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`} data-testid="email-settings-toast">
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          <span className="text-sm">{toast.msg}</span>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && !settings ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 flex items-center gap-3">
            <AlertCircle size={20} />
            <div>
              <p className="font-medium">Could not load settings</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-6">
            {/* Provider picker */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Server size={18} className="text-gray-500" /> Email Provider
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(settings?.available_providers || []).map((p) => (
                  <label key={p.key}
                         className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${
                           provider === p.key ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                         }`}
                         data-testid={`provider-${p.key}`}>
                    <input type="radio" name="provider" checked={provider === p.key}
                           onChange={() => setProvider(p.key)} className="mt-1" />
                    <div>
                      <div className="font-medium text-gray-900">{p.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5 font-mono">{p.key === 'microsoft' ? 'Microsoft Graph API (OAuth 2.0)' : `${p.host}:${p.port} (STARTTLS)`}</div>
                    </div>
                  </label>
                ))}
              </div>
              {PROVIDER_HELP[provider] && (
                <p className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="provider-help">
                  {PROVIDER_HELP[provider]}
                </p>
              )}
            </div>

            {/* Account details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Shield size={18} className="text-gray-500" /> Sender Account
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sender Email Address</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                         className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         data-testid="sender-email"
                         placeholder="e.g. pratik.primeaccts@gmail.com" />
                  <p className="text-xs text-gray-500 mt-1">OTPs and notifications are sent from this address.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sender Display Name</label>
                  <input type="text" value={fromName} onChange={(e) => setFromName(e.target.value)}
                         className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                         data-testid="sender-name"
                         placeholder="e.g. PrimeAIF Fund Manager" />
                </div>
              </div>
              {provider === 'microsoft' ? (
                <MicrosoftGraphFields
                  tenantId={msTenantId} setTenantId={setMsTenantId}
                  clientId={msClientId} setClientId={setMsClientId}
                  secret={msClientSecret} setSecret={setMsClientSecret}
                  secretSet={settings?.ms_client_secret_set}
                />
              ) : (
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    App Password {settings?.password_set && <span className="text-xs text-green-600 font-normal">(currently set — leave blank to keep)</span>}
                  </label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password}
                           onChange={(e) => setPassword(e.target.value)}
                           className="w-full px-3 py-2 pr-10 border rounded-lg font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                           data-testid="sender-password"
                           placeholder={settings?.password_set ? '••••••••••••••••' : 'Enter app password'}
                           autoComplete="new-password" />
                    <button type="button" onClick={() => setShowPassword(s => !s)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            data-testid="toggle-password-visibility">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Never shown after saving. Stored securely and used only for SMTP sign-in.</p>
                </div>
              )}
              {settings?.updated_at && (
                <p className="text-xs text-gray-400">
                  Last updated {new Date(settings.updated_at).toLocaleString()} {settings.updated_by ? `by ${settings.updated_by}` : ''}
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3">
              <button type="submit" disabled={saving}
                      className="inline-flex items-center px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
                      data-testid="save-email-settings">
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
              <div className="flex items-center gap-2 flex-1 min-w-[280px]">
                <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                       className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                       data-testid="test-email-input"
                       placeholder="Test recipient (blank = your email)" />
                <button type="button" onClick={handleTest} disabled={testing}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-300 text-sm whitespace-nowrap"
                        data-testid="send-test-email">
                  <Send size={14} className="mr-1.5" />
                  {testing ? 'Sending...' : 'Send Test'}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OwnerEmailSettings;
